/* eslint-disable no-useless-concat */
import React, { useState, useEffect, useCallback } from "react";
import { Spacer, Row, Tooltip, Loading, Modal, Text, Button as NextButton, Textarea, Col } from '@nextui-org/react';
import { Upload, TwoUsers, Send, Chat, Unlock, EditSquare, Delete, Download, Show, ArrowRightSquare, Location } from 'react-iconly'
import { CopyToClipboard } from "react-copy-to-clipboard";
import Select, { Async } from "react-select";
import { debounce } from "lodash";

import { secureFetch, fetchAuthed } from "../../lib/api";
import endpoint from "../../config/endpoint.json";
import { error, success, warning } from "../../lib/userAlerts";
import { searchOrderList } from "../../lib/utils";

import FileUpload from "../FileUpload";
import CannedMessageModal from "./SendCannedMessageComponent";

import dayjs from '../../lib/day'
import { CopyIcon } from "../../icons/copy";

/**
 *
 * List Card Elements   
 *
 *
 */

const LocationDetails = ({ dispText }) => (
  <div className="flex items-center gap-2 w-full">
    <div className="flex-shrink-0">
      <Location 
        set="bulk" 
        size={20} 
        primaryColor="#4F46E5" // indigo-600
        className="transform hover:scale-110 transition-transform duration-200"
      />
    </div>
    <div className="flex-1 min-w-0">
      <Text 
        className="text-gray-700 text-sm font-medium truncate hover:text-indigo-600 transition-colors duration-200"
        title={dispText} // Shows full text on hover
      >
        {dispText}
      </Text>
    </div>
  </div>
);

/****
 *
 * Download file
 *
 */

const downloadURI = (blob, name, documentType) => {
  // IE doesn't allow using a blob object directly as link href
  // instead it is necessary to use msSaveOrOpenBlob

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob);
    return;
  }

  let fileObj;

  if (documentType === "pdf") {
    fileObj = new Blob([blob], { type: "application/pdf" });
  } else {
    fileObj = new Blob([blob], { type: "image/jpeg" });
  }

  const uri = window.URL.createObjectURL(fileObj);
  const link = document.createElement("a");

  link.download = name;
  link.href = uri;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    // For Firefox it is necessary to delay revoking the ObjectURL
    document.body.removeChild(link);
    window.URL.revokeObjectURL(uri);
  }, 100);
};

function getFileKey(name) {
  return Math.random().toString(32).substring(2).toUpperCase() + "_" + name;
}

/**
 *
 * Upload attachments
 *
 */

const uploadDocument = async (state, setState, key, file, getShipmentList, searchOrderNo, searchBolNumber) => {
  try {
    const preSignedUrlResponse = await fetchAuthed(
      `${endpoint.signedDocumentUploadUrl}?key=${key}`, 
      { method: "POST" }
    );
    const preSignedUrl = await preSignedUrlResponse.json();

    const uploadResponse = await fetch(preSignedUrl.s3_logo_url, {
      method: "PUT",
      headers: { "content-type": "application/octet-stream" },
      body: file,
    });

    if (!uploadResponse.ok) throw new Error('Upload failed');

    const [ok] = await secureFetch(endpoint.save_documents, {
      stopId: state.stopId,           // Now correctly using Stop ID
      orderId: state.orderNo,
      stopSequence: state.stopSequence, // Now correctly using Stop Sequence
      documentName: key,
      documentType: state.selectedDocType,
      realDocumentType: file.type === "application/pdf" ? "pdf" : file.type,
    });

    if (!ok) throw new Error('Unable to save to database');

    success("Successfully attached documents to order id :- " + state.orderNo);
    
    // Refresh the shipment list to show the new document
    getShipmentList(searchOrderNo, searchBolNumber);
    
    setState(prev => ({ 
      ...prev, 
      uploadModal: false,
      uploadProgress: false,
      uploadAttachment: null,
      selectedDocType: null
    }));
  } 
  catch (e) {
    console.error(e);
    error("Oops! Could't upload the document. Please try later :(");
    setState(prev => ({ 
      ...prev, 
      uploadProgress: false
    }));
  }
};

const CancelOrReAssignClick = ({ onClick, dispText, dispLable }) => (
  <NextButton
    auto
    css={{br: 0}}
    title={dispLable || "Reassign Documents"}
    onClick={onClick}
  >
    {dispText}
  </NextButton>
);

const ListItems = (props) => {
  // State management
  const [state, setState] = useState({
    openCannedMessageModal: false,
    stopId: props.stopId,        // This is the Stop ID
    stopSequence: props.stop,        // This is the Stop Sequence
    orderNo: props.ordderNo,
    openFirstModal: false,
    openThirdModal: false,
    document: props.documents,
    array: [],
    imageUrl: "",
    selectedDocument: [],
    stopIdList: [],
    orderId: "",
    selectedStopSequence: "",
    stopSequenceList: [],
    allDocuments: [],
    artifactsList: props.artifactsList || [],
    activeArtifactsList: props.activeArtifactsList || [],
    uploadModal: false,
    downloadModal: false,
    selectedDocType: props.activeArtifactsList?.[0]?.type_name || "Select Document Type",
    attachmentUrl: "",
    dialogTitle: "Move documents to another order or to different stop in this order",
    reassingLinkClick: false,
    reassignLink: "Reassign",
    searchOrderNo: props.searchOrderNo,
    searchBolNumber: props.searchBolNumber,
    toolTipText: null,
    uploadAttachment: null,
    uploadProgress: false,
    downloadWarningDialog: false,
    openDriverDetailsModal: false,
    reopenLoading: false,
    driver1Details: {},
    driver2Details: {},
    loadingDriverDetails: false,
    showCommentsModal: false,
  });

  // Update state when props change
  useEffect(() => {
    setState(prev => ({
      ...prev,
      stopId: props.stopId,        // Stop ID
      stopSequence: props.stop,        // Stop Sequence
      orderNo: props.ordderNo,
      orderNoList: props.orderNoList || [],
      artifactsList: props.artifactsList || [],
      searchOrderNo: props.searchOrderNo,
      searchBolNumber: props.searchBolNumber,
      selectedDocType: props.activeArtifactsList?.[0]?.type_name || "Select Document Type",
      activeArtifactsList: props.activeArtifactsList || [],
    }));
  }, [props]);

  const closeUploadModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      uploadModal: false,
      documentTypeNotSelected: false,
      selectedDocType: "Select Document Type",
    }));
  }, []);

  const closeCannedMessageModal = useCallback(() => {
    setState(prev => ({ ...prev, openCannedMessageModal: false }));
    props.getShipmentList(state.searchOrderNo, state.searchBolNumber);
  }, [props, state.searchOrderNo, state.searchBolNumber]);

  const openUploadModal = useCallback(() => {
    setState(prev => ({
      ...prev,
      attachmentUrl: "",
      uploadModal: true,
      selectedDocType: state.activeArtifactsList?.[0]?.type_name || "Select Document Type",
    }));
  }, [state.activeArtifactsList]);

  const openThirdModal = useCallback(() => {
    if (state.orderId && state.stopId) {
      setState(prev => ({
        ...prev,
        openThirdModal: true,
        dialogTitle: "Confirmation",
      }));
    } else {
      warning("Please select order id and stop id");
    }
  }, [state.orderId, state.stopId]);

  const toggleDialogf = useCallback(() => {
    setState(prev => ({
      ...prev,
      openFirstModal: false,
      openThirdModal: false,
      reassingLinkClick: false,
      dialogTitle: "Move documents to another order or to different stop in this order",
      reassignLink: "Reassign",
      selectedDocument: [],
      stopId: "",
      orderId: "",
      selectedStopSequence: "",
      AssignAllDocumentButtonClicked: false,
    }));
  }, []);

  const toggleDownloadWarningDialog = useCallback(() => {
    setState(prev => ({ ...prev, downloadWarningDialog: !prev.downloadWarningDialog }));
  }, []);

  const toggleDriverDetailsModal = useCallback(async () => {
    if (!state.openDriverDetailsModal) {
      setState(prev => ({
        ...prev,
        openDriverDetailsModal: !prev.openDriverDetailsModal,
        loadingDriverDetails: true,
      }));

      let driver1Details = {};
      let driver2Details = {};

      let [driver1Ok, driver1Status, driver1Json] = await secureFetch(
        endpoint.getDriverDetails,
        {
          clientId: props.clientId,
          driverId: props.driver1EmpId,
          driverUserId: props.driver1UserId,
        }
      );

      if (driver1Ok) {
        driver1Details = driver1Json.driverDetails[0]
          ? driver1Json.driverDetails[0]
          : {};
      }

      let [driver2Ok, driver2Status, driver2Json] = await secureFetch(
        endpoint.getDriverDetails,
        {
          clientId: props.clientId,
          driverId: props.driver2EmpId,
          driverUserId: props.driver2UserId,
        }
      );

      if (driver2Ok) {
        driver2Details = driver2Json.driverDetails[0]
          ? driver2Json.driverDetails[0]
          : {};
      }

      setState(prev => ({
        ...prev,
        driver1Details,
        driver2Details,
        loadingDriverDetails: false,
      }));
    } else {
      setState(prev => ({
        ...prev,
        openDriverDetailsModal: !prev.openDriverDetailsModal,
        loadingDriverDetails: false,
      }));
    }
  }, [props]);

  const getOrderNO = useCallback((input, callback) => {
    let orderNumbers = [];

    // If our state is not empty
    if(state.orderNoList?.length > 0){
      orderNumbers = searchOrderList(state.orderNoList, input)
    }

    // If order exists in our cache
    if(orderNumbers?.length > 0){
      callback(null, {
        options: orderNumbers,
        // CAREFUL! Only set this to true when there are no more options,
        // or more specific queries will not be sent to the server.
        complete: true
      });
    }
    else{
      // Now fetch orders from the API.
      secureFetch(endpoint.getOrderNO, {
        filter: input,
        os: state.OS,
        osFilterRequired: false,
      })
      .then(([ok, status, json]) => {
        if (ok) {
          // Update state for future search.
          setState(prev => ({
            ...prev,
            orderNoList: json.orderNo.map((k) => ({
              label: k.order_id,
              value: k.order_id,
            }))
          }));

          orderNumbers = json.orderNo.map((k) => ({
            label: k.order_id,
            value: k.order_id,
          }))
        } else {
          error("Oops! Unable to fetch order number list.");
        }
      })
      .finally(()=>{
        callback(null, {
          options: orderNumbers,
          // CAREFUL! Only set this to true when there are no more options,
          // or more specific queries will not be sent to the server.
          complete: true
        });
      });
    }
  }, [state.orderNoList, state.OS]);

  // on change of the stop sequence
  const handleChangeStopSeq = useCallback((selectedOption) => {
    const selectedStopSequence = selectedOption ? selectedOption.value : "";
    setState(prev => ({ ...prev, selectedStopSequence }));
  }, []);

  // on change of stop id auto select stop sequence
  const handleChange = useCallback((selectedOption) => {
    const stopId = selectedOption ? selectedOption.value : "";
    const stopSequenceList = [];

    selectedOption.stopSequences.forEach((o) => {
      stopSequenceList.push({ label: o.sequence, value: o.sequence });
    });

    const selectedStopSequence =
      stopSequenceList.length > 0
        ? stopSequenceList[0].value
        : state.selectedStopSequence;
        
    setState(prev => ({ ...prev, stopId, stopSequenceList, selectedStopSequence }));
  }, [state.selectedStopSequence]);

  // on change of the order id ...
  const handleChangeR = useCallback(async (selectedOption) => {
    const orderId = selectedOption ? selectedOption.value : "";
    setState(prev => ({ ...prev, orderId }));

    const stopIdList = [];
    const stopSequenceList = [];
    let stopId = state.stopId;
    let selectedStopSequence = state.selectedStopSequence;

    // filter the whole list based on order id
    // and generate the stopIdList and stopSequenceList accordingly
    try{
      let [ok, status, stopList] = await  secureFetch(endpoint.getStopIdList, {orderId: orderId })

      if(stopList.success){
        
        // List of stops!
        stopList.stopId.forEach(o => {
          stopIdList.push({
            label: o.stop_id,
            value: o.stop_id,
            stopSequences: JSON.parse(o.stop_sequence)
          });
        });

        // assign the first value in the list for stop id
        if (stopIdList.length > 0) {
          stopId = stopIdList[0].value;
          stopIdList[0].stopSequences.forEach((o) => {
            stopSequenceList.push({ label: o.sequence, value: o.sequence });
          });
        }

        // assign the first value in the list for stop sequence
        if (stopSequenceList.length > 0) {
          selectedStopSequence = stopSequenceList[0].value;
        }

        console.log(stopSequenceList)
      }
      else{
        error("Unable to retrieve the stop list for this order.")
      }
    }
    catch(e) {
      console.log(e)
      error("Unable to retrieve the stop list for this order.")
    }

    setState(prev => ({
      ...prev,
      stopIdList,
      stopId,
      stopSequenceList,
      selectedStopSequence,
    }));

  }, [state.stopId, state.selectedStopSequence]);

  const reassignAllDocuments = useCallback(() => {
    let realList = props.documents;
    let documentTypeList = [];
    let allDocumentList = [];
    Object.keys(realList).forEach((key) => {
      const doc = realList[key];
      documentTypeList.push(doc);
    });

    documentTypeList.forEach((item) => {
      item.forEach((items) => {
        allDocumentList.push(items);
      });
    });
    
    // Ensure current order is in the orderNoList
    const currentOrderOption = {
      label: props.ordderNo,
      value: props.ordderNo
    };
    
    setState(prev => {
      // Add current order to orderNoList if it's not already there
      const orderNoList = prev.orderNoList || [];
      const orderExists = orderNoList.some(order => order.value === props.ordderNo);
      const updatedOrderNoList = orderExists ? orderNoList : [...orderNoList, currentOrderOption];
      
      return {
        ...prev,
        selectedDocument: allDocumentList,
        openFirstModal: true,
        AssignAllDocumentButtonClicked: true,
        // Initialize with current order values
        orderId: state.orderNo,           // Set current order ID
        stopId: props.stopId,          // Set current stop ID  
        selectedStopSequence: props.stop,  // Set current stop sequence
        orderNoList: updatedOrderNoList,   // Ensure current order is in the list
      };
    });
  }, [props.documents, props.ordderNo, props.stopId, props.stop]);

  const reassignDocuments = useCallback(() => {
    let stopId = state.stopId;
    let orderId = state.orderId;
    let stopSequence = state.selectedStopSequence;
    state.selectedDocument.forEach((items) => {
      let id = items.id;
      secureFetch(endpoint.reassignDocuments, {
        id,
        stopId,
        orderId,
        stopSequence,
      }).then(([ok, status, json]) => {
        if (ok) {
          success("Successfully reassigned documents to order id : " + orderId);
          setState(prev => ({ selectedDocument: [] }));
          props.getShipmentList(
            state.searchOrderNo,
            state.searchBolNumber
          );
        } else {
          error("Oops! Could't reassign documents :(");
        }
      });
    });

    setState(prev => ({
      ...prev,
      openFirstModal: false,
      openThirdModal: false,
      reassingLinkClick: false,
      dialogTitle: "Move documents to another order or to different stop in this order",
      reassignLink: "Reassign",
      selectedDocument: [],
      stopId: "",
      orderId: "",
    }));
  }, [state.selectedDocument, state.stopId, state.orderId, state.selectedStopSequence, state.searchOrderNo, state.searchBolNumber, props]);

  const handleUpload = useCallback((file, email) => {
    setState(prev => ({ ...prev, documentTypeNotSelected: false }));
    if (
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "application/pdf"
    ) {
      setState(prev => ({ ...prev, uploadAttachment: file }));
      return {
        sub(handlers) {
          handlers.start();
          // Show loading for 1.5 seconds to give proper feedback
          setTimeout(() => {
            handlers.end();
          }, 1500);
        },
      };
    } else {
      setState(prev => ({ ...prev, documentTypeNotSelected: true }));
      return {
        sub(handlers) {
          handlers.error();
        },
      };
    }
  }, []);

  const uploadAttachment = useCallback(() => {
    setState(prev => ({ ...prev, uploadProgress: true }));
    const file = state.uploadAttachment;
    
    // Add null check for file
    if (!file) {
      setState(prev => ({ ...prev, uploadProgress: false }));
      alert("Please select a file first");
      return;
    }
    
    const self = state;
    let key = getFileKey(file.name);
    
    // Call the actual upload function
    uploadDocument(self, setState, key, file, props.getShipmentList, state.searchOrderNo, state.searchBolNumber)
      .then(() => {
        // Resolve the upload promise if it exists
        if (state.uploadPromise) {
          state.uploadPromise.resolve();
          setState(prev => ({ ...prev, uploadPromise: null }));
        }
      })
      .catch(() => {
        // Reject the upload promise if it exists
        if (state.uploadPromise) {
          state.uploadPromise.reject();
          setState(prev => ({ ...prev, uploadPromise: null }));
        }
      });
  }, [state, props.getShipmentList]);

  const imageDownload = useCallback((e, documentType, documentName, origDocName) => {
    if (!state.reassingLinkClick) {
      let fileName = "";
      let r = origDocName;

      //remove the file extension.
      r = r.substring(0, r.lastIndexOf("."));

      setState(prev => ({ ...prev, downloadModal: true }));

      if (documentType === "pdf") {
        fileName = "SDN_Driver_Connect_Artifax_Download" + " - " + r + ".pdf";
      } else {
        let x = documentType.split("/");
        fileName =
          "SDN_Driver_Connect_Artifax_Download" + " - " + r + "." + x[1];
      }

      fetch(documentName)
        .then((res) => res.blob()) // Gets the response and returns it as a blob
        .then((blob) => {
          setState(prev => ({ ...prev, downloadModal: false }));
          downloadURI(blob, fileName, documentType);
        });
    }
  }, [state.reassingLinkClick]);

  const deleteAttachment = useCallback((attachmentId) => {
    secureFetch(endpoint.deleteAttachment, {
      attachmentId,
    }).then(([ok, status, json]) => {
      if (ok) {
        success("You have successfully deleted attachment");
        props.getShipmentList(
          state.searchOrderNo,
          state.searchBolNumber
        );
      } else {
        error("Oops! error occour during delete attachment :(");
      }
    });
  }, [state.searchOrderNo, state.searchBolNumber, props]);

  const reopenShipment = useCallback(() => {
    setState(prev => ({ ...prev, reopenLoading: true }));

    secureFetch(endpoint.reopenShipment, {
      sdnRecordId: props.sdnRecordId
    }).then(([ok, status, json]) => {
      if (ok) {
        success("You have successfully re-opened this shipment");
        props.getShipmentList(
          props.ordderNo,
          state.searchBolNumber
        );
        setState(prev => ({ ...prev, reopenLoading: false }));
      } else {
        error("Oops! error occured during reopen this shipment");
        setState(prev => ({ ...prev, reopenLoading: false }));
      }
    });
  }, [props.sdnRecordId, props.ordderNo, state.searchBolNumber, props]);

  const onCopy = useCallback(() => {
    success("Copied to clipboard")
  }, []);

  const driverDetailsModal = useCallback(() => {
    return (
      <Modal
          width="500px"
          closeButton
          blur
          aria-labelledby="Driver Details"
          open={state.openDriverDetailsModal}
          onClose={toggleDriverDetailsModal}
      >
          <Modal.Header>
            <Text size="1.25rem">
              Driver details for Stop {props.stopId}, Sequence {props.stop}
            </Text>
          </Modal.Header>
          <Modal.Body>
              <div className="pa3">
              {state.loadingDriverDetails ? (
                <div className="flex justify-center items-center">
                  <Loading size="md">Fetching driver details..</Loading>
                </div>
              ) : (
              <div>
                <div>
                  <div className="f4">Driver 1 Details</div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Name:</div>
                    <div className="f5">
                      {state.driver1Details?.name || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver1Details?.name || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Employee Id:</div>
                    <div className="f5">
                      {state.driver1Details?.employee_id || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver1Details?.employee_id || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Email:</div>
                    <div className="f5">
                      {state.driver1Details?.user_email || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver1Details?.user_email || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Phone:</div>
                    <div className="f5">
                      {state.driver1Details?.phone || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver1Details?.phone || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                </div>
                <div className="mt3">
                  <div className="f4">Driver 2 Details</div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Name:</div>
                    <div className="f5">
                      {state.driver2Details?.name || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver2Details?.name || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Employee Id:</div>
                    <div className="f5">
                      {state.driver2Details?.employee_id || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver2Details?.employee_id || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Email:</div>
                    <div className="f5">
                      {state.driver2Details?.user_email || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver2Details?.user_email || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                  <div className="flex flex-row mt2 items-center">
                    <div className="f5 w-30">Phone:</div>
                    <div className="f5">
                      {state.driver2Details?.phone || "Not available"}
                    </div>
                    <CopyToClipboard
                      text={state.driver2Details?.phone || ""}
                      onCopy={onCopy}
                    >
                      <span className="ml2">
                        <CopyIcon height={16} width={16}/>
                      </span>
                    </CopyToClipboard>
                  </div>
                </div>
              </div>
              )}
            </div>
          </Modal.Body>
      </Modal>
    );
  }, [state.openDriverDetailsModal, state.loadingDriverDetails, state.driver1Details, state.driver2Details, onCopy]);

  const renderDownloadInfoModal = useCallback(() => {
    if (!state.downloadModal) {
      return false;
    }

    return (
      <Modal
        blur
        closeButton
        open={true}
        onClose={() => {
          setState(prev => ({ ...prev, downloadModal: false }));
        }}
      >
        <Modal.Header>
          <Text size={20}>Downloading ... </Text>
        </Modal.Header>
        <Modal.Body>
          <Text size={18}>
            In most cases, the file will saved in the {" "} <strong>'Downloads'</strong> folder. Please check there.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <NextButton 
            size='sm'
            auto
            css={{br:0}}
            ghost
            onClose={() => {
              setState(prev => ({ ...prev, downloadModal: false }));
            }} 
          >Close</NextButton>
        </Modal.Footer>
      </Modal>
    );
  }, [state.downloadModal]);

  const renderUploadModal = useCallback(() => {
    if (!state.uploadModal) {
      return false;
    }

    return (
      <Modal
        width="480px"
        open={state.uploadModal}
        onClose={closeUploadModal}
        blur
        css={{br: 0}}
        closeButton
      >
        <Modal.Header>
          <Text size="1.25rem">Upload a document</Text>
        </Modal.Header>

        <Modal.Body>
          <Text size="1rem">Select a document type</Text>
          <Row justify="space-between" align="center" css={{ marginBottom: '20px' }}>
            <Col span={16}>
              <select
                className="select select-sm select-primary w-full"
                value={state.selectedDocType}
                onChange={(e) =>
                  setState(prev => ({ ...prev, selectedDocType: e.target.value }))
                }
              >
                {(state.artifactsList || []).map((item, id) => {
                  if (item.status !== 0) {
                    return (
                      <option key={id} value={item.type_name}>
                        {item.type_name}
                      </option>
                    );
                  } else {
                    return false;
                  }
                })}
              </select>
            </Col>
          </Row>

          <Row justify="space-between" align="center" css={{ marginBottom: '20px' }}>
            <Col span={16}>
              <FileUpload defaultName={null} uploadCb={handleUpload} />
            </Col>
            <Col span={8} css={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <NextButton
                auto  
                color="primary"
                disabled={state.uploadProgress || !state.selectedDocType || !state.uploadAttachment}
                css={{ 
                  minWidth: '80px',
                  height: '40px', // Match the FileUpload component height
                  marginLeft: '10px'
                }}
                onClick={() => {
                  if (!state.selectedDocType) {
                    alert('Please select a document type');
                    return;
                  }
                  if (!state.uploadAttachment) {
                    alert('Please select a file first');
                    return;
                  }
                  uploadAttachment();
                }}
              >
                {state.uploadProgress? (
                  <>
                    <Loading size="sm" color="primary" />
                  </>
                ) : (
                  'Upload'
                )}
              </NextButton>
            </Col>
          </Row>
          {state.documentTypeNotSelected ? (
            <div className="flex flex-row pa3 bg-near-white">
              <Text
                b
                size={16}
                color="red"
              >
                Please select image or pdf file
              </Text>
            </div>
          ) : (
            <div className="h1 mt2 ml4" />
          )}

        </Modal.Body>
      </Modal>
    );
  }, [state.uploadModal, state.selectedDocType, state.artifactsList, state.uploadProgress, state.uploadAttachment, handleUpload, closeUploadModal, uploadAttachment]);

  const renderCloseDate = useCallback(() => {
    //Handle bad DB data just in case 
    let closeDate = props.autoCloseDate ? props.autoCloseDate : new Date().getTime() / 1000
    let driverClosed = false

    if(props.driverCloseTs && props.driverCloseTs >= closeDate){
      driverClosed = true
      closeDate = props.driverCloseTs
    }

    return (
      <div className="pa2">
        <Text size="1.25rem" b color={driverClosed ? 'error': 'primary'}>
          {driverClosed? 'Closed by Driver': 'Auto Closed by System'}
        </Text>
        <Spacer y={0.5}/>
        <Text size="1rem">
          {`Order closed ${dayjs.unix(closeDate).fromNow()}.`}
        </Text>
        <Text size="1rem">
          {`Closed time stamp is ${dayjs.unix(closeDate).utc().format('MM/DD/YYYY h:mm A')} (UTC).`}
        </Text>
        <Spacer y={0.5}/>
        <Text size="1rem" span b>Please click on lock icon (button) to re-open the shipment.</Text>
      </div>
    )
  }, [props.autoCloseDate, props.driverCloseTs]);

  const renderActionButtons = useCallback(() => {
    let reOpenButtonColor = props.driverCloseTs >= props.autoCloseDate ? '$red500': 'primary'

    return (
      <div className="flex flex-column items-end" style={{zIndex: 0}}>
        <div
          className={
            props.allowSendMessage === 1
              ? "bg-red flex justify-center items-center white"
              : "flex justify-center items-center white"
          }
          style={{
            width: 30,
            height: 20,
            borderRadius: 5,
            position: "relative",
            top: 10,
            right: 0,
            zIndex: 100,
          }}
        >
          {props.allowSendMessage === 1
            ? props.pushNotificationCount || 0
            : null}
        </div>
        
        <NextButton.Group ghost rounded={false} style={{ zIndex: 99 }}>
          {((props.checkOs === 0 &&
            Object.keys(props.documents).length !== 0) ||
            (props.checkOs === 1 &&
              props.documents[props.osdDocType])) && ( 
                <NextButton
                  onClick={reassignAllDocuments}
                  icon={<EditSquare set="light" fill="currentColor" />}
                />
          )}

          <NextButton
            onClick={openUploadModal}
            icon={<Upload set="light" fill="currentColor" />}
          />
          
          {(props.allowBrokersToReopenClosedStop === 1 && props.autoCloseInd === 1) && (
            <Tooltip placement="right" content={renderCloseDate()}>
              <NextButton 
                css={{br: 0, borderLeft: 'none', color: reOpenButtonColor }}
                onClick={reopenShipment}
                icon={<Unlock set="light" />} 
              >
              {state.reopenLoading ? ( <Loading type="points" color="primary" size="md" /> ) : null}
              </NextButton>
            </Tooltip>
          )}

          <NextButton
            css={{br: 0, borderLeft: 'none'}}
            icon={<Chat set="light" fill="currentColor" />}
            onClick={() => setState(prev => ({ ...prev, showCommentsModal: true }))}
          />

          <NextButton
            onClick={toggleDriverDetailsModal}
            icon={<TwoUsers set="light" fill="currentColor" />}
          />

          {props.allowSendMessage === 1 && (
            <NextButton
              icon={<Send set="light" fill="currentColor" />}
              onClick={() => {
                setState(prev => ({ openCannedMessageModal: true }));
              }}
            />
          )}
        </NextButton.Group>

      </div>
    );
  }, [props.checkOs, props.documents, props.osdDocType, reassignAllDocuments, openUploadModal, state.reopenLoading, props.allowBrokersToReopenClosedStop, props.autoCloseInd, props.driverCloseTs, props.autoCloseDate, props.pushNotificationCount, renderCloseDate, toggleDriverDetailsModal, props.allowSendMessage]);

  const renderArtifaxSection = useCallback(() => {
    return (
      <div className="w-70">
        <div className="card bg-white shadow-md rounded-lg border border-gray-200 hover:shadow-lg transition-shadow duration-200">
          <div className="card-body p-4">
            {/* Header Section */}
            <div className="flex flex-row items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="card-title text-indigo-600 text-lg font-bold tracking-tight hover:text-indigo-700 transition-colors duration-200">
                  {props.ordderNo}
                </h2>
                <span className="text-xs px-2 py-1 bg-indigo-50 text-indigo-600 rounded-full border border-indigo-100">
                  Stop {props.stop}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500 font-medium bg-gray-50 px-2 py-1 rounded">
                  Stop ID: {props.stopId}
                </span>
                <span className="text-sm text-gray-400">
                  {props.shipmentTime}
                </span>
              </div>
            </div>

            {/* Address Section */}
            <div className="flex flex-row items-center justify-between mb-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
              <div className="flex items-center gap-2 text-gray-700 w-full">
                <LocationDetails dispText={props.address} />
              </div>
            </div>

            {/* Partner Document Source */}
            {props.partnerDocument && (
              <div className="flex flex-row items-center gap-2 mb-3 p-2 bg-red-50 rounded-lg border border-red-100">
                <span className="text-xs text-red-600 font-semibold">
                  Record Source: {props.recordSource}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="card-actions justify-end mt-2 border-t border-gray-100 pt-2">
              {renderActionButtons()}
            </div>
          </div>
        </div>
      </div>
    );
  }, [props.ordderNo, props.stopId, props.address, props.recordSource, props.partnerDocument, props.stop, props.shipmentTime, renderActionButtons]);

  const showDocuments = useCallback(() => {
    return (props.checkOs === 0 &&
      Object.keys(props.documents).length !== 0) ||
      (props.checkOs === 1 &&
        props.documents[props.osdDocType]) ? (
      <div className="w-100 mt1">
        {Object.keys(props.documents).map((key) => {
          if (
            (props.checkOs === 0 && key !== props.osdDocType) ||
            (props.checkOs === 1 && key === props.osdDocType)
          ) {
            const doc = props.documents[key];
            return doc.map((attachment) => {
              return (
                <div className="w-100" key={attachment.id}>
                  <div className="mt1 flex flex-column justify-between w-100 flex-wrap bb pb2">
                    <div className="w-100 f5">{key}</div>
                    <div className="w-100 flex justify-between">
                      <Show
                        color='blue'
                        set='light'
                        size={24}
                        onClick={() => {
                          if (attachment.origDocName) {
                            props.setPreview(attachment.documentName);
                          } else {
                            toggleDownloadWarningDialog();
                          }
                        }}
                      />
                      <Download
                        color='green'
                        set='light'
                        size={24}
                        onClick={
                          attachment.origDocName
                            ? imageDownload.bind(
                                null,
                                `${attachment.documentName}`,
                                attachment.documentType,
                                attachment.documentName,
                                attachment.origDocName
                              )
                            : toggleDownloadWarningDialog
                        }
                      />
                      <ArrowRightSquare
                        size={24}
                        onClick={() => {
                          let document = [];
                          document.push(attachment);
                          setState(prev => ({
                            ...prev,
                            orderId: state.orderNo,
                            stopId: props.stopId,
                            selectedDocument: document,
                            openFirstModal: true
                          }));
                        }}
                      />
                      <Tooltip
                        css={{br: 0}}
                        trigger='click'
                        placement="top"
                        content={       
                          <div className="pa3">
                            <h5 className="mb3">Do you want to delete this attachment?</h5>
                            <NextButton
                              auto
                              ghost
                              size='sm'
                              css={{br: 0}}
                              color='error'
                              onClick={() => {
                                deleteAttachment(attachment.id);
                              }}
                            >
                              Yes
                            </NextButton>
                          </div>
                        }
                      >
                        <Delete set='light' size={24} color='red'/>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              );
            });
          }
        })}
      </div>
    ) : (
      <div className="mt2 mr4">None</div>
    );
  }, [props.checkOs, props.documents, props.osdDocType, imageDownload, toggleDownloadWarningDialog, deleteAttachment]);

  const renderRatingSection = useCallback(() => {
    return (
      <div className="w-30 bg-near-white">
        <div className="flex flex-column pl3 pr3 pb2 pt2 w-100">
          <Text h5>Documents</Text>
          {props.activeArtifactsList.length !== 0 ? (
            showDocuments()
          ) : (
            <div className="mt2 mr4">None</div>
          )}
        </div>
      </div>
    );
  }, [props.activeArtifactsList, showDocuments]);

  const renderReassignModal = useCallback(() => {
    if (!state.openFirstModal) {
      return false;
    }

    // Format the current order for display in the dropdown
    const currentOrderOption = state.orderNo ? {
      label: state.orderId,
      value: state.orderId
    } : null;

    // Create initial options that include the current order
    const initialOrderOptions = state.orderNoList || [];
    if (state.orderId && !initialOrderOptions.some(opt => opt.value === state.orderId)) {
      initialOrderOptions.unshift(currentOrderOption);
    }

    // Format the current stop ID for display
    const currentStopOption = state.stopId ? {
      label: state.stopId,
      value: state.stopId,
      stopSequences: [{ sequence: state.selectedStopSequence }]
    } : null;

    // Format the current stop sequence for display
    const currentStopSequenceOption = state.selectedStopSequence ? {
      label: state.selectedStopSequence,
      value: state.selectedStopSequence
    } : null;

    return (
      <Modal
        width='400px'
        closeButton
        blur
        aria-labelledby="Re-Assign docs"
        open={state.openFirstModal}
        onClose={toggleDialogf}
        css={{
          height: '600px',
          overflow: 'auto',
        }}
      >
      <Modal.Header>
          <Text id="modal-title" size={20}>
            {state.dialogTitle}
          </Text>
      </Modal.Header>

      <Modal.Body>
        <div className="flex">
          {state.openThirdModal ? 
          (
            <div className="w-100">
              <div className="text-center mb-4">
                <Text size="1rem" b>
                  Reassign Documents to
                </Text>
              </div>
              
              <table className="table table-bordered w-100">
                <tbody>
                  <tr className="f5">
                    <td className="pa2 fw5">Order Id</td>
                    <td className="pa2 fw6">{state.orderId}</td>
                  </tr>
                  <tr className="f5">
                    <td className="pa2 fw5">Stop Id</td>
                    <td className="pa2 fw6">{state.stopId}</td>
                  </tr>
                  <tr className="f5">
                    <td className="pa2 fw5">Stop Sequence</td>
                    <td className="pa2 fw6">{state.selectedStopSequence}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : 
          (
            <div className="">
              <label>Order ID</label>
              <Async
                name="form-field-name"
                clearable={false}
                style={{ width: "350px", marginTop: "10px" }}
                placeholder="Select Order Id"
                value={currentOrderOption}
                onChange={handleChangeR}
                loadOptions={debounce(getOrderNO, 500)}
                defaultOptions={initialOrderOptions}
              />
              <div className="py-3">
                <label>Stop ID</label>
              </div>
              <Select
                name="form-field-name"
                clearable={false}
                style={{ width: "350px" }}
                placeholder="Select Stop Id"
                value={currentStopOption}
                onChange={handleChange}
                options={state.stopIdList}
              />
              <div className="py-3">
                <label>Stop Sequence</label>
              </div>
              <Select
                name="form-field-name"
                clearable={false}
                style={{ width: "350px" }}
                placeholder="Select Stop Sequence"
                value={currentStopSequenceOption}
                onChange={handleChangeStopSeq}
                options={state.stopSequenceList}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {state.openThirdModal ? (
          <div className="w-100 flex justify-end">
            <NextButton
              auto
              ghost
              color="error"
              css={{br: 0}}
              title="Cancel"
              onClick={toggleDialogf}
            >
              Cancel
            </NextButton>
            <Spacer x={1}/>
            <CancelOrReAssignClick
              onClick={reassignDocuments}
              dispText="Confirm"
            />
          </div>
        ) : (
          <div className="flex w-100 justify-end">
            <CancelOrReAssignClick
              onClick={openThirdModal}
              dispText="Reassign"
            />
          </div>
        )}
      </Modal.Footer>
    </Modal>
    );
  }, [state.openFirstModal, state.openThirdModal, state.orderId, state.stopId, state.selectedStopSequence, state.orderNoList, state.stopIdList, state.stopSequenceList, handleChangeR, getOrderNO, handleChange, handleChangeStopSeq, reassignDocuments, toggleDialogf, openThirdModal]);

  const renderCommentsModal = useCallback(() => {
    return (
      <Modal
        width="500px"
        closeButton
        blur
        aria-labelledby="Driver Comments"
        open={state.showCommentsModal}
        onClose={() => setState(prev => ({ ...prev, showCommentsModal: false }))}
      >
        <Modal.Header>
          <Text size="1.25rem">Driver Comments</Text>
        </Modal.Header>
        <Modal.Body>
          <Textarea
            bordered
            css={{ br: 0, w: '100%' }}
            className="w-100"
            disabled={true}
            minRows={4}
            rows={4}
            value={props.comments ? props.comments : "None"}
          />
        </Modal.Body>
        <Modal.Footer>
          <NextButton
            auto
            css={{br: 0}}
            onClick={() => setState(prev => ({ ...prev, showCommentsModal: false }))}
          >
            Close
          </NextButton>
        </Modal.Footer>
      </Modal>
    );
  }, [state.showCommentsModal, props.comments]);

  return (
    <div className="flex flex-row ba b--black-10 ma2 br2 mb2">
      {renderArtifaxSection()}
      {renderRatingSection()}
      {renderReassignModal()}

      {renderUploadModal()}
      {renderDownloadInfoModal()}
      {driverDetailsModal()}
      {renderCommentsModal()}
      {props.allowSendMessage === 1 && (
        <CannedMessageModal
          recordId={props.sdnRecordId}
          driver1Id={props.driver1EmpId}
          driver2Id={props.driver2EmpId}
          driver1UserId={props.driver1UserId}
          driver2UserId={props.driver2UserId}
          orderId={props.ordderNo}
          bolNumber={props.bolNumber}
          stopId={props.stopId}
          clientId={props.clientId}
          closeCannedMessageModal={closeCannedMessageModal}
          openCannedMessageModal={state.openCannedMessageModal}
          partnerDocument={props.partnerDocument}
        />
      )}
      <Modal
        blur
        width="400px"
        open={state.downloadWarningDialog}
        onClose={toggleDownloadWarningDialog}
        css={{ height: "150px", br: 0 }}
      >
        <Modal.Header>
          <Text b size={20}>Document getting processed</Text>
        </Modal.Header>
        <Modal.Body>
          We are processing your document. Please try after about 5 minutes.
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ListItems;
